import { store } from "@store";
import { IResponseAutosuggest, IResponseFetchDetail, IResponseMountRoute, IResponseRoute } from "@types";
import { getLocation, getRouteIdFromUrl, getDnGroupIdFromUrl } from "@utils";
import api from ".";

export const fetchAutosuggest = async (text: string) => {
  const { lat, lon } = await getLocation();
  const response = await api.get<IResponseAutosuggest>("/v2/routes/autosuggest", {
    params: {
      lat,
      lon,
      text,
    },
    timeout: 60000,
  });
  return response?.data?.items || [];
};

export const fetchAutosuggestByCoords = async (lat: number, lon: number) => {
  try {
    const response = await api.get<IResponseAutosuggest>("/v2/routes/autosuggest", {
      params: { lat, lon, text: `${lat}, ${lon}` },
    });
    return response?.data?.items?.[0]?.title || "Ponto de passagem";
  } catch (error) {
    console.error("Erro ao buscar nome do local:", error);
    return "Ponto de passagem";
  }
};

export const mountRoute = async (body: any) => {
  const response = await api.post<IResponseMountRoute>("/v2/routes/mount", body);
  return response.data;
};

export const createAndSendRoute = async (body: any) => {
  const userIdAmity = store.getState().login.userIdAmity;
  const response = await api.post("v2/routes/rio/send", body, {
    headers: {
      "unique-profile-id": userIdAmity || "",
    },
  });
  return response.data;
};

export const deleteRoute = async (id: any) => {
  const response = await api.put("v2/routes/id");
  return response.data;
};

export const getData = async (page: any, size: any, search: any, order: any, orderBy: String) => {
  const response = await api.get<IResponseRoute>("/list", {
    params: {
      page,
      size,
      search,
      order,
      orderBy,
    },
  });
  return response?.data || [];
};

export const fetchRoutesList = async (
  page: number,
  size: number,
  search?: string,
  sortB?: string,
  fieldSort?: string,
  options?: { signal?: AbortSignal; prioritizeIds?: string[]; routeAreaId?: string }
) => {
  const params = new URLSearchParams();
  const sizeSend = size > 10 ? size : 10;
  params.append("size", sizeSend.toString());

  if (page >= 0) {
    params.append("page", page.toString());
  }

  const names = {
    name: "routeName",
    origin: "originName",
    destination: "destinationName",
    stops: "quantityStops",
    distance: "distance",
    duration: "duration",
    register: "routeName",
    vwcoin: "routeName",
    createdAt: "creationDate",
    performance: "routeName",
  };

  if (search) {
    params.append("search", search);
  }

  if (sortB) {
    params.append("order", sortB.toUpperCase());
  }

  if (fieldSort && fieldSort in names) {
    params.append("orderBy", names[fieldSort as keyof typeof names]);
  }

  if (options?.prioritizeIds) {
    params.append("prioritizeIds", options.prioritizeIds.join(","));
  }

  if (options?.routeAreaId) {
    params.append("routeAreaId", options?.routeAreaId);
  }

  const response = await api.get(`v2/routes/list?${params.toString()}`, {
    signal: options?.signal,
  });
  return response.data;
};

export const fetchDetail = async (id: string) => {
  const response = await api.get<IResponseFetchDetail>(`v2/routes/route/${id}`);
  return response.data;
};

export const fetchHasMechanicalEmergency = async (routeId: string): Promise<boolean> => {
  const response = await api.get(`v2/routes/${routeId}/has-mechanical-emergency`);
  return response?.data?.data?.result ?? false;
};

export const updateRoute = async (body: any) => {
  const response = await api.patch(`v2/routes/route/${body.id}`, body);
  return response.data;
};

export const updateRouteForTrip = async (body: any) => {
  const userIdAmity = store.getState().login.userIdAmity;
  const response = await api.post(`v2/routes/rio/send/trip`, body, {
    headers: {
      "unique-profile-id": userIdAmity || "",
    },
  });
  return response.data;
};

export const excludeRoute = async (id: string) => {
  const response = await api.put(`v2/routes/${id}`);
  return response.data;
};

export const fetchVehicleList = async (page: number, size: number, search?: string, sortB?: string) => {
  const params = new URLSearchParams();
  const sizeSend = size > 10 ? size : 10;
  params.append("size", sizeSend.toString());
  params.append("page", page.toString());

  if (search) {
    params.append("search", search);
  }

  if (sortB) {
    params.append("order", sortB.toUpperCase());
  }

  const response = await api.get(`v2/routes/vehicles/list?${params.toString()}`);
  return response.data;
};

export const fetchDriverList = async (page: number, size: number, search?: string, sortB?: string) => {
  const params = new URLSearchParams();
  params.append("size", size.toString());
  params.append("page", page.toString());

  if (search) {
    params.append("search", search);
  }

  if (sortB) {
    params.append("order", sortB.toUpperCase());
  }

  const response = await api.get(`v2/routes/drives/list?${params.toString()}`);
  return response.data;
};

export const fetchDriverListV1 = async (options?: { applyUrlFilters?: boolean }) => {
  const applyUrlFilters = options?.applyUrlFilters ?? true;

  // Cada parâmetro só é enviado quando existir na URL; caso contrário, é omitido.
  const params: Record<string, string> = {};
  if (applyUrlFilters) {
    const routeId = getRouteIdFromUrl();
    const dnGroupId = getDnGroupIdFromUrl();
    if (routeId) params["route-id"] = routeId;
    if (dnGroupId) params["dn-group-id"] = dnGroupId;
  }

  const response = await api.get(`v1/fleets/drivers/rio`, {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return response.data;
};

export const fetchRoutesWayNotifications = async () => {
  const response = await api.get(`v2/routes/notificationOutRoute`);
  return response.data;
};

export const fetchVehiclesWithTags = async (
  tagId: string,
  page: number = 1,
  size: number = 10,
  chassisFilter?: string,
  vehicleType?: string,
  search?: string
) => {
  const params = new URLSearchParams();
  
  params.append("page", page.toString());
  params.append("size", size.toString());
  
  if (chassisFilter) {
    params.append("chassisFilter", chassisFilter);
  }
  
  if (vehicleType) {
    params.append("vehicleType", vehicleType);
  }
  
  if (search) {
    params.append("search", search);
  }

  const response = await api.get(`v2/routes/vehicles/tags/${tagId}/list?${params.toString()}`);
  return response.data;
};

export const fetchVehicleTags = async (
  page: number = 1,
  size: number = 10,
  order: string = "ASC",
  orderBy: string = "id",
  chassisFilter?: string,
  vehicleType?: string,
  search?: string
) => {
  const params = new URLSearchParams();
  
  params.append("page", page.toString());
  params.append("size", size.toString());
  params.append("order", order);
  params.append("orderBy", orderBy);
  
  if (chassisFilter) {
    params.append("chassisFilter", chassisFilter);
  }
  
  if (vehicleType) {
    params.append("vehicleType", vehicleType);
  }
  
  if (search) {
    params.append("search", search);
  }
  
  const response = await api.get(`v2/routes/vehicles/tags?${params.toString()}`);
  return response.data;
};

export const fetchUrltripShare = async (tripId: string) => {
  const response = await api.get(`v2/routes/${tripId}/share`);
  return response.data;
};

export const fetchTripPublic = async (uuid: string) => {
  const response = await api.get(`v1/public/routes/${uuid}/shared`);
  return response.data;
};

export const fetchAddressWithLatLong = async (lat?: number, long?: number) => {
  const response = await api.get(`v1/routes/trips/geolocation?latitude=${lat}&longitude=${long}`);
  return response.data;
};

export const getStopsCheckpoint = async () => {
  const response = await api.get(`v2/checkpoints/types`);
  return response.data;
};

export const fetchRouteOptimze = async (body: any) => {
  const response = await api.post(`v2/routes/optimize`, body);
  return response.data;
};

export const fetchVehiclesUptime = async ( ) => {
  const response = await api.get(`https://api.non-prod.latam-uptime-gateway.rio.cloud/v1/assets/group/all`);
  return response.data;
}